/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Cloudflare Turnstile for the owner-notes bot wall (contract §5, #18/#42): the SG page
 * renders the widget and hands its token upstream on device mint, publish and resubmit;
 * DealHunt verifies it strictly. A token is single-use, so `getToken()` resets and
 * re-executes the widget every call.
 *
 * `execution: 'execute'` + `appearance: 'interaction-only'`: nothing renders unless
 * Cloudflare needs the reader to interact, in which case the challenge appears inside the
 * container the band provides (never a detached overlay).
 *
 * Without `NEXT_PUBLIC_OWNER_TURNSTILE_SITE_KEY` the hook resolves an empty token — local
 * DealHunt with OWNER_TURNSTILE_ALLOW_INSECURE accepts it; production DealHunt answers
 * 503 `owner_notes_unavailable`, which the band shows as "not available right now".
 */
import { useCallback, useEffect, useRef } from 'react'
import { publicConfig } from 'config'

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const EXECUTE_TIMEOUT_MS = 30_000

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      execution: 'render' | 'execute'
      appearance: 'always' | 'execute' | 'interaction-only'
      callback: (token: string) => void
      'error-callback': (code?: string) => void
      'expired-callback': () => void
      'timeout-callback': () => void
    }
  ) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/** A rendered widget and the node it was rendered into (a re-mounted node needs a new one). */
interface RenderedWidget {
  api: TurnstileApi
  id: string
  container: HTMLElement
}

let scriptPromise: Promise<TurnstileApi> | null = null

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = () => {
        if (window.turnstile) {
          resolve(window.turnstile)
        } else {
          reject(new Error('turnstile'))
        }
      }
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('turnstile'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export class TurnstileError extends Error {
  constructor() {
    super("We couldn't run the bot check — reload the page and try again.")
    this.name = 'TurnstileError'
  }
}

export interface TurnstileHandle {
  /** Where an interactive challenge renders when Cloudflare asks for one. */
  containerRef: React.RefObject<HTMLDivElement>
  /** A fresh single-use token ('' when no site key is configured). */
  getToken: () => Promise<string>
  /** True when the widget is configured at all (so the UI can say so in copy). */
  enabled: boolean
}

/**
 * Whether a Turnstile site key is configured. Production DealHunt refuses a publish without
 * a token, so every owner-note ASK (rail, Owners section, the declared tile) hides behind
 * this — a reader is never walked through three steps to fail at the last click. Same
 * value on the server and in the browser (NEXT_PUBLIC), so SSR and hydration agree.
 */
export function turnstileConfigured(): boolean {
  return (publicConfig('ownerTurnstileSiteKey') ?? '').length > 0
}

export function useTurnstile(): TurnstileHandle {
  const siteKey = publicConfig('ownerTurnstileSiteKey') ?? ''
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<RenderedWidget | null>(null)
  const pendingRef = useRef<{ resolve: (t: string) => void; reject: (e: Error) => void } | null>(
    null
  )
  const enabled = siteKey.length > 0

  useEffect(
    () => () => {
      const widget = widgetRef.current
      if (widget) {
        try {
          widget.api.remove(widget.id)
        } catch {
          // already gone
        }
        widgetRef.current = null
      }
    },
    []
  )

  const ensureWidget = useCallback(async (): Promise<RenderedWidget | null> => {
    const container = containerRef.current
    if (!siteKey || !container) {
      return null
    }
    const current = widgetRef.current
    if (current && current.container === container && container.isConnected) {
      return current
    }
    // The container was re-mounted (the band left step 3 — Back, or a result then "Edit and
    // resend" — and came back): the old widget sits on a detached node and never calls
    // back, so every publish timed out as "couldn't run the bot check". Drop it, render anew.
    if (current) {
      try {
        current.api.remove(current.id)
      } catch {
        // already gone with its node
      }
      widgetRef.current = null
    }
    const api = await loadTurnstile()
    const settle = (token: string | null): void => {
      const pending = pendingRef.current
      pendingRef.current = null
      if (!pending) {
        return
      }
      if (token) {
        pending.resolve(token)
      } else {
        pending.reject(new TurnstileError())
      }
    }
    const id = api.render(container, {
      sitekey: siteKey,
      execution: 'execute',
      appearance: 'interaction-only',
      callback: token => settle(token),
      // Surface Cloudflare's code: 110200 = hostname not on the site key's allowlist
      // (the usual local-dev failure), 300xxx = challenge failures.
      'error-callback': code => {
        console.warn('[owner-notes] turnstile error', code)
        settle(null)
      },
      'expired-callback': () => settle(null),
      'timeout-callback': () => settle(null),
    })
    widgetRef.current = { api, id, container }
    return widgetRef.current
  }, [siteKey])

  const getToken = useCallback(async (): Promise<string> => {
    if (!siteKey) {
      return ''
    }
    let widget: RenderedWidget | null
    try {
      widget = await ensureWidget()
    } catch {
      throw new TurnstileError()
    }
    if (!widget) {
      throw new TurnstileError()
    }
    const ready = widget
    return new Promise<string>((resolve, reject) => {
      // A holder, not a `let`: the slot's callbacks clear the timer that is started after it.
      const clock: { timer?: ReturnType<typeof setTimeout> } = {}
      const slot = {
        resolve: (token: string) => {
          clearTimeout(clock.timer)
          resolve(token)
        },
        reject: (error: Error) => {
          clearTimeout(clock.timer)
          reject(error)
        },
      }
      clock.timer = setTimeout(() => {
        // Only this call's slot — a second getToken (double-clicked Publish) that has since
        // taken the slot must not be orphaned by the first one's timer.
        if (pendingRef.current === slot) {
          pendingRef.current = null
        }
        reject(new TurnstileError())
      }, EXECUTE_TIMEOUT_MS)
      pendingRef.current = slot
      try {
        ready.api.reset(ready.id)
        ready.api.execute(ready.id)
      } catch {
        clearTimeout(clock.timer)
        pendingRef.current = null
        reject(new TurnstileError())
      }
    })
  }, [siteKey, ensureWidget])

  return { containerRef, getToken, enabled }
}
